import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Monitor, X } from 'lucide-react';

export default function DevModeBanner() {
  const [viewportTest, setViewportTest] = useState('auto');
  const [showMobileFrame, setShowMobileFrame] = useState(false);
  const [isOn, setIsOn] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  
  // Check if we're inside an iframe (mobile preview)
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;
  
  useEffect(() => {
    // Check localStorage for user preference
    const saved = localStorage.getItem('corvana_dev_mode_visual');
    const savedMain = localStorage.getItem('corvana_dev_mode');
    
    // Default to ON for preview environments
    if (saved === 'false' || savedMain === 'false') {
      setIsOn(false);
    } else {
      setIsOn(true);
    }
    
    // Show on ANY non-production domain
    // This includes: localhost, emergent, preview, staging, any IP address
    const hostname = window.location.hostname;
    const isProduction = hostname === 'corvana.com.au' || hostname === 'www.corvana.com.au';
    
    // ALWAYS show on non-production domains
    if (!isProduction) {
      setIsVisible(true);
    }
  }, []);

  const handleToggle = () => {
    const newState = !isOn;
    setIsOn(newState);
    localStorage.setItem('corvana_dev_mode_visual', String(newState));
    localStorage.setItem('corvana_dev_mode', String(newState));
    window.location.reload();
  };

  const handleViewportChange = (mode) => {
    setViewportTest(mode);
    if (mode === 'mobile') {
      setShowMobileFrame(true);
    } else {
      setShowMobileFrame(false);
    }
  };

  // Hide in iframe completely
  if (isInIframe) {
    return null;
  }

  // Don't render on production
  if (!isVisible) {
    return null;
  }

  // If turned off, show minimal floating button to re-enable
  if (!isOn) {
    return (
      <div className="fixed top-4 right-4 z-[99999]">
        <button
          onClick={handleToggle}
          className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-4 py-2 rounded-full shadow-lg text-sm transition-all hover:scale-105"
        >
          🛠️ Enable Dev Mode
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Preview Frame */}
      {showMobileFrame && (
        <div className="fixed inset-0 z-[200000] bg-slate-900/95 flex items-center justify-center p-4">
          <div className="relative">
            <button
              onClick={() => {
                setShowMobileFrame(false);
                setViewportTest('auto');
              }}
              className="absolute -top-12 right-0 flex items-center gap-2 text-white/80 hover:text-white"
            >
              <span className="text-sm">Close Preview</span>
              <X className="w-5 h-5" />
            </button>
            
            <div className="bg-slate-800 rounded-[3rem] p-3 shadow-2xl">
              <div className="absolute top-6 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 rounded-full z-10" />
              <div className="relative bg-white rounded-[2.5rem] overflow-hidden" style={{ width: '375px', height: '812px' }}>
                <iframe
                  src={window.location.href}
                  className="w-full h-full border-0"
                  title="Mobile Preview"
                  style={{ width: '375px', height: '812px', borderRadius: '2.5rem' }}
                />
              </div>
            </div>
            
            <div className="text-center mt-4 text-white/60 text-sm">
              iPhone 14 Pro • 375 × 812
            </div>
          </div>
        </div>
      )}
      
      {/* Main Dev Banner - BIG AND VISIBLE */}
      <div 
        className="fixed top-0 left-0 right-0 z-[99999] flex items-center justify-between px-4 md:px-8"
        style={{ 
          height: '52px',
          backgroundColor: '#f59e0b',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        {/* Left: DEV MODE Label + Toggle */}
        <div className="flex items-center gap-3 md:gap-5">
          <span className="text-white font-black text-base md:text-lg tracking-wide flex items-center gap-2">
            🛠️ <span className="hidden sm:inline">DEV MODE</span><span className="sm:hidden">DEV</span>
          </span>
          
          {/* Big Toggle Switch */}
          <button
            onClick={handleToggle}
            className="relative inline-flex items-center rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white"
            style={{
              width: '56px',
              height: '28px',
              backgroundColor: isOn ? '#22c55e' : 'rgba(255,255,255,0.3)'
            }}
            title="Click to turn OFF DEV MODE"
          >
            <span
              className="inline-block rounded-full bg-white shadow-lg transition-transform"
              style={{
                width: '24px',
                height: '24px',
                transform: isOn ? 'translateX(30px)' : 'translateX(2px)'
              }}
            />
          </button>
          
          <span className="text-white font-bold text-sm hidden md:inline">
            {isOn ? '✓ ON' : 'OFF'}
          </span>
        </div>
        
        {/* Center: Viewport Buttons */}
        <div className="hidden lg:flex items-center gap-2">
          <button 
            onClick={() => handleViewportChange('auto')}
            className="px-5 py-2 rounded-lg font-semibold text-sm transition-all"
            style={{
              backgroundColor: viewportTest === 'auto' ? 'white' : 'rgba(255,255,255,0.2)',
              color: viewportTest === 'auto' ? '#f59e0b' : 'white'
            }}
          >
            Auto
          </button>
          <button 
            onClick={() => handleViewportChange('mobile')}
            className="px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2"
            style={{
              backgroundColor: viewportTest === 'mobile' ? 'white' : 'rgba(255,255,255,0.2)',
              color: viewportTest === 'mobile' ? '#f59e0b' : 'white'
            }}
          >
            <Smartphone className="w-4 h-4" />
            Mobile
          </button>
          <button 
            onClick={() => handleViewportChange('desktop')}
            className="px-4 py-2 rounded-lg font-semibold text-sm transition-all flex items-center gap-2"
            style={{
              backgroundColor: viewportTest === 'desktop' ? 'white' : 'rgba(255,255,255,0.2)',
              color: viewportTest === 'desktop' ? '#f59e0b' : 'white'
            }}
          >
            <Monitor className="w-4 h-4" />
            Desktop
          </button>
        </div>
        
        {/* Right: Navigation Buttons */}
        <div className="flex items-center gap-2 md:gap-3">
          <Link to="/login">
            <button 
              className="px-3 md:px-5 py-2 rounded-lg font-semibold text-sm transition-all hover:bg-white hover:text-amber-600"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            >
              Login
            </button>
          </Link>
          <Link to="/app">
            <button 
              className="px-3 md:px-5 py-2 rounded-lg font-semibold text-sm transition-all hidden md:block hover:bg-white hover:text-amber-600"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            >
              Dashboard
            </button>
          </Link>
          <Link to="/techadmin">
            <button 
              className="px-3 md:px-5 py-2 rounded-lg font-bold text-sm transition-all hover:bg-amber-100"
              style={{ backgroundColor: 'white', color: '#f59e0b' }}
            >
              Tech Admin
            </button>
          </Link>
        </div>
      </div>
    </>
  );
}
